import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { query } from '@warp-drive/utilities/json-api';
import type { Store } from '@warp-drive/core';
import type RouterService from '@ember/routing/router';
import type RemoteEventLoggerService from 'tio-employee/services/remote-event-logger';
import type SessionContextService from '../../../services/session-context';
import type { FinancialInstitution as FinancialInstitutionModel } from 'tio-data/data/resources/financial-institution.type';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';
import type ReimaginedViewService from 'tio-employee/services/reimagined-view';
import type { ReimaginedDocumentCenterModel } from 'tio-employee/components/reimagined/production/document-center';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type ObservabilityUploadRouteModel = Resolved<
  ReturnType<AuthenticatedObservabilityUploadRoute['model']>
>;

export default class AuthenticatedObservabilityUploadRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare remoteEventLogger: RemoteEventLoggerService;
  @service declare reimaginedView: ReimaginedViewService;

  async model() {
    const person = this.sessionContext.user.person;

    const { content: fiContent } = (await this.store.request(
      query<FinancialInstitutionModel>('financial-institution', {
        'filter[isActive]': true,
        'filter[hasObservabilityInstructions]': true,
      })
    )) as { content: { data: FinancialInstitutionModel[] } };

    const financialInstitutions = [...fiContent.data].sort(
      (a: FinancialInstitutionModel, b: FinancialInstitutionModel) => a.name?.localeCompare(b.name)
    );

    this.remoteEventLogger.logObservabilityEvent({
      eventName: 'INITIALIZE',
      component: 'AuthenticatedObservabilityUploadRoute',
    });

    // When reimagined view is enabled, fetch document data for the Document Center
    if (this.reimaginedView.isEnabled) {
      const personId = person.id;
      let observableDocuments: ObservableDocumentModel[] = [];

      if (personId) {
        try {
          const { content: docContent } = (await this.store.request(
            query<ObservableDocumentModel>('observable-document', {
              'filter[person]': personId,
              include: 'observable-asset',
              sort: '-created-at',
              'page[limit]': 25,
            })
          )) as { content: { data: ObservableDocumentModel[] } };
          observableDocuments = [...docContent.data];
        } catch {
          // Fail gracefully — document center will show empty state
        }
      }

      const reimagined: ReimaginedDocumentCenterModel = {
        observableDocuments,
      };

      return { person, financialInstitutions, reimagined };
    }

    return { person, financialInstitutions };
  }
}
